import { useCallback, useMemo, useRef, useState } from "react";
import { useStore } from "../store";
import {
  abortMultipartUpload,
  completeMultipartUpload,
  getMultipartUploadStatus,
  initiateMultipartUpload,
  MultipartUploadInitiateData,
  presignMultipartUploadParts,
  UploadedFileReference,
} from "../services/uploadServices";
import {
  clearPersistedMultipartUpload,
  getPersistedMultipartUpload,
  mergePersistedUploadedParts,
  PersistedMultipartUploadState,
  reconcilePersistedMultipartSession,
  setPersistedMultipartUpload,
  UploadFileFingerprint,
} from "../services/uploadStateStorage";
import { buildFileChunks, getFileChunk } from "../utils/fileChunk";

export type FrontendUploadStatus =
  | "IDLE"
  | "INITIATING"
  | "UPLOADING"
  | "PAUSED"
  | "COMPLETING"
  | "COMPLETED"
  | "FAILED"
  | "ABORTED";

export interface MultipartUploadProgressState {
  status: FrontendUploadStatus;
  bytesUploaded: number;
  totalBytes: number;
  percentage: number;
  currentPart: number;
  totalParts: number;
  speedBytesPerSecond?: number;
  estimatedRemainingMs?: number;
  error?: string | null;
  file?: File | null;
  fileMetadata?: UploadedFileReference | null;
  resumable?: boolean;
}

export interface MultipartUploadContext {
  context: string;
  ownerId?: string | null;
  metadata?: Record<string, any>;
}

interface UseMultipartUploadOptions {
  scope: string;
}

const uploadMode = (process.env.REACT_APP_UPLOAD_MODE ?? "legacy").toLowerCase();
const multipartEnabled = uploadMode === "multipart";
const allowLegacyFallback =
  (process.env.REACT_APP_UPLOAD_ALLOW_LEGACY_FALLBACK ?? "false").toLowerCase() === "true";
const maxConcurrency = Math.max(
  1,
  Number.parseInt(process.env.REACT_APP_MULTIPART_UPLOAD_CONCURRENCY ?? "4", 10) || 4
);
const maxPartRetries = Math.max(
  1,
  Number.parseInt(process.env.REACT_APP_MULTIPART_UPLOAD_PART_RETRIES ?? "3", 10) || 3
);

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const calculateFingerprint = (file: File): UploadFileFingerprint => ({
  name: file.name,
  size: file.size,
  lastModified: file.lastModified,
  type: file.type,
});

const sameFingerprint = (left?: UploadFileFingerprint | null, right?: UploadFileFingerprint | null) =>
  Boolean(left && right) &&
  left?.name === right?.name &&
  left?.size === right?.size &&
  left?.lastModified === right?.lastModified &&
  left?.type === right?.type;

const isExpired = (expiresAt?: string | null) => {
  if (!expiresAt) {
    return false;
  }
  const parsed = Date.parse(expiresAt);
  return Number.isNaN(parsed) ? false : parsed <= Date.now();
};

const buildEmptyState = (): MultipartUploadProgressState => ({
  status: "IDLE",
  bytesUploaded: 0,
  totalBytes: 0,
  percentage: 0,
  currentPart: 0,
  totalParts: 0,
  error: null,
  file: null,
  fileMetadata: null,
  resumable: false,
});

const buildPersistedState = (
  scope: string,
  file: File,
  session: MultipartUploadInitiateData,
  uploadedParts: PersistedMultipartUploadState["uploadedParts"],
  context?: Record<string, any>
): PersistedMultipartUploadState => {
  const now = new Date().toISOString();

  return {
    localUploadId: `${scope}:${session.fileId}`,
    scope,
    fileFingerprint: calculateFingerprint(file),
    fileId: session.fileId,
    uploadId: session.uploadId,
    storageKey: session.storageKey,
    partSize: session.partSize,
    totalParts: session.totalParts,
    uploadedParts,
    expiresAt: session.expiresAt,
    status: uploadedParts.length > 0 ? "UPLOADING" : "INITIATED",
    context: context ?? null,
    createdAt: now,
    updatedAt: now,
  };
};

export const useMultipartUpload = ({ scope }: UseMultipartUploadOptions) => {
  const { user } = useStore();
  const [state, setState] = useState<MultipartUploadProgressState>(() => {
    const persisted = getPersistedMultipartUpload(scope);

    if (!persisted) {
      return buildEmptyState();
    }

    return {
      ...buildEmptyState(),
      status: persisted.status === "COMPLETED" ? "COMPLETED" : "PAUSED",
      totalBytes: persisted.fileFingerprint.size,
      percentage:
        persisted.totalParts > 0
          ? Math.round((persisted.uploadedParts.length / persisted.totalParts) * 100)
          : 0,
      currentPart: persisted.uploadedParts.length,
      totalParts: persisted.totalParts,
      resumable: true,
    };
  });

  const runIdRef = useRef(0);
  const activeSessionRef = useRef<PersistedMultipartUploadState | null>(
    getPersistedMultipartUpload(scope)
  );
  const uploadPromiseRef = useRef<Promise<UploadedFileReference | null> | null>(null);
  const uploadAbortControllersRef = useRef<Set<AbortController>>(new Set());

  const persistSession = useCallback(
    (session: PersistedMultipartUploadState | null) => {
      activeSessionRef.current = session;

      if (session) {
        setPersistedMultipartUpload(scope, session);
      } else {
        clearPersistedMultipartUpload(scope);
      }
    },
    [scope]
  );

  const setUploadState = useCallback(
    (next: Partial<MultipartUploadProgressState>) => {
      setState((current) => ({
        ...current,
        ...next,
      }));
    },
    []
  );

  const reset = useCallback(() => {
    runIdRef.current += 1;
    uploadPromiseRef.current = null;
    uploadAbortControllersRef.current.forEach((controller) => controller.abort());
    uploadAbortControllersRef.current.clear();
    persistSession(null);
    setState(buildEmptyState());
  }, [persistSession]);

  const abort = useCallback(async () => {
    runIdRef.current += 1;
    uploadPromiseRef.current = null;
    uploadAbortControllersRef.current.forEach((controller) => controller.abort());
    uploadAbortControllersRef.current.clear();

    const session = activeSessionRef.current;
    if (session) {
      await abortMultipartUpload(
        {
          fileId: session.fileId,
          uploadId: session.uploadId,
        },
        user?.token
      );
    }

    persistSession(null);
    setState({
      ...buildEmptyState(),
      status: "ABORTED",
    });
  }, [persistSession, user?.token]);

  const uploadPart = useCallback(
    async (
      resolveURL: (attempt: number) => Promise<string>,
      blob: Blob,
      partNumber: number,
      signalRunId: number
    ) => {
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= maxPartRetries; attempt += 1) {
        if (signalRunId !== runIdRef.current) {
          throw new Error("upload aborted");
        }

        try {
          const url = await resolveURL(attempt);
          const controller = new AbortController();
          uploadAbortControllersRef.current.add(controller);
          const response = await fetch(url, {
            method: "PUT",
            body: blob,
            headers: {
              "Content-Type": blob.type || "application/octet-stream",
            },
            signal: controller.signal,
          }).finally(() => {
            uploadAbortControllersRef.current.delete(controller);
          });

          if (!response.ok) {
            throw new Error(`Upload da parte ${partNumber} falhou (${response.status})`);
          }

          const etag =
            response.headers.get("ETag") ?? response.headers.get("etag") ?? "";
          if (!etag) {
            throw new Error(`ETag ausente na parte ${partNumber}`);
          }

          return etag.replaceAll('"', "");
        } catch (error: any) {
          if (error?.name === "AbortError") {
            throw new Error("upload aborted");
          }
          lastError = error instanceof Error ? error : new Error("upload part failed");

          if (attempt >= maxPartRetries) {
            break;
          }

          await sleep(Math.min(300 * 2 ** (attempt - 1), 2000));
        }
      }

      throw lastError ?? new Error("upload part failed");
    },
    []
  );

  const startUpload = useCallback(
    async (file: File, context: MultipartUploadContext): Promise<UploadedFileReference | null> => {
      if (!multipartEnabled) {
        return null;
      }

      const fingerprint = calculateFingerprint(file);
      const currentRunId = runIdRef.current + 1;
      runIdRef.current = currentRunId;

      setUploadState({
        ...buildEmptyState(),
        status: "INITIATING",
        totalBytes: file.size,
        file,
        error: null,
      });

      const persisted = getPersistedMultipartUpload(scope);
      let session = persisted && sameFingerprint(persisted.fileFingerprint, fingerprint)
        ? persisted
        : null;

      if (persisted && !session) {
        clearPersistedMultipartUpload(scope);
      }

      if (session) {
        const remoteStatusResponse = await getMultipartUploadStatus(session.fileId, user?.token);
        if (remoteStatusResponse.data !== false) {
          session = reconcilePersistedMultipartSession(session, remoteStatusResponse.data);

          if (session) {
            persistSession(session);
          } else {
            persistSession(null);
          }
        } else if (isExpired(session.expiresAt)) {
          persistSession(null);
          session = null;
        }
      }

      if (!session) {
        const initiateResponse = await initiateMultipartUpload(
          {
            fileName: file.name,
            mimeType: file.type || "application/octet-stream",
            size: file.size,
            context: context.context,
            ownerId: context.ownerId,
            metadata: context.metadata,
          },
          user?.token
        );

        if (initiateResponse.data === false) {
          setUploadState({
            status: "FAILED",
            totalBytes: file.size,
            file,
            error: initiateResponse.message,
          });

          if (!allowLegacyFallback) {
            throw new Error(initiateResponse.message);
          }

          return null;
        }

        session = buildPersistedState(
          scope,
          file,
          initiateResponse.data,
          [],
          context.metadata
        );
        persistSession(session);
      } else {
        const restoredUploadedParts = mergePersistedUploadedParts(session.uploadedParts);
        setUploadState({
          status: "PAUSED",
          totalBytes: file.size,
          percentage:
            session.totalParts > 0
              ? Math.round((restoredUploadedParts.length / session.totalParts) * 100)
              : 0,
          currentPart: restoredUploadedParts.length,
          totalParts: session.totalParts,
          file,
          resumable: true,
        });
      }

      const chunks = buildFileChunks(file.size, session.partSize);
      const uploadedParts = new Map<number, string>(
        mergePersistedUploadedParts(session.uploadedParts).map((part) => [part.partNumber, part.etag])
      );

      const startedAt = Date.now();
      const initialUploadedBytes = session.uploadedParts.reduce((total, uploadedPart) => {
        const chunk = chunks.find((item) => item.partNumber === uploadedPart.partNumber);
        return total + (chunk?.size ?? 0);
      }, 0);
      const uploadedBytesRef = { current: initialUploadedBytes };

      setUploadState({
        status: "UPLOADING",
        totalBytes: file.size,
        bytesUploaded: initialUploadedBytes,
        percentage: file.size > 0 ? Math.round((initialUploadedBytes / file.size) * 100) : 0,
        currentPart: uploadedParts.size,
        totalParts: session.totalParts,
        file,
        resumable: uploadedParts.size > 0,
      });

      const pendingChunks = chunks.filter((chunk) => !uploadedParts.has(chunk.partNumber));

      for (let index = 0; index < pendingChunks.length; index += maxConcurrency) {
        const batch = pendingChunks.slice(index, index + maxConcurrency);
        const presignResponse = await presignMultipartUploadParts(
          {
            fileId: session.fileId,
            uploadId: session.uploadId,
            parts: batch.map((chunk) => chunk.partNumber),
          },
          user?.token
        );

        if (presignResponse.data === false) {
          setUploadState({
            status: "FAILED",
            error: presignResponse.message,
          });
          throw new Error(presignResponse.message);
        }

        const urlByPart = new Map(
          presignResponse.data.parts.map((part) => [part.partNumber, part.url] as const)
        );

        await Promise.all(
          batch.map(async (chunk) => {
            const chunkBlob = getFileChunk(file, chunk);

            const etag = await uploadPart(
              async (attempt) => {
                if (attempt === 1) {
                  const initialURL = urlByPart.get(chunk.partNumber);
                  if (initialURL) {
                    return initialURL;
                  }
                }

                const refreshed = await presignMultipartUploadParts(
                  {
                    fileId: session!.fileId,
                    uploadId: session!.uploadId,
                    parts: [chunk.partNumber],
                  },
                  user?.token
                );
                if (refreshed.data === false || refreshed.data.parts.length === 0) {
                  throw new Error(refreshed.message || `Falha ao renovar URL da parte ${chunk.partNumber}`);
                }
                return refreshed.data.parts[0].url;
              },
              chunkBlob,
              chunk.partNumber,
              currentRunId
            );

            uploadedParts.set(chunk.partNumber, etag);
            uploadedBytesRef.current += chunk.size;

            if (currentRunId !== runIdRef.current) {
              throw new Error("upload aborted");
            }

            const bytesUploadedNow = uploadedBytesRef.current;
            const elapsedMs = Math.max(1, Date.now() - startedAt);
            const speedBytesPerSecond = Math.round((bytesUploadedNow * 1000) / elapsedMs);
            const remainingBytes = Math.max(0, file.size - bytesUploadedNow);
            const estimatedRemainingMs =
              speedBytesPerSecond > 0
                ? Math.round((remainingBytes / speedBytesPerSecond) * 1000)
                : undefined;

            setUploadState({
              status: "UPLOADING",
              bytesUploaded: bytesUploadedNow,
              totalBytes: file.size,
              percentage: Math.min(100, Math.round((bytesUploadedNow / file.size) * 100)),
              currentPart: uploadedParts.size,
              totalParts: session!.totalParts,
              speedBytesPerSecond,
              estimatedRemainingMs,
              error: null,
            });

            persistSession({
              ...session!,
              uploadedParts: Array.from(uploadedParts.entries())
                .map(([partNumber, savedEtag]) => ({ partNumber, etag: savedEtag }))
                .sort((left, right) => left.partNumber - right.partNumber),
              status: "UPLOADING",
              updatedAt: new Date().toISOString(),
            });
          })
        );
      }

      setUploadState({
        status: "COMPLETING",
        bytesUploaded: file.size,
        totalBytes: file.size,
        percentage: 100,
        currentPart: session.totalParts,
        totalParts: session.totalParts,
        file,
      });

      const completeResponse = await completeMultipartUpload(
        {
          fileId: session.fileId,
          uploadId: session.uploadId,
          parts: Array.from(uploadedParts.entries())
            .map(([partNumber, etag]) => ({ partNumber, etag }))
            .sort((left, right) => left.partNumber - right.partNumber),
        },
        user?.token
      );

      if (completeResponse.data === false) {
        setUploadState({
          status: "FAILED",
          error: completeResponse.message,
          file,
        });
        throw new Error(completeResponse.message);
      }

      uploadAbortControllersRef.current.clear();
      persistSession(null);
      setUploadState({
        status: "COMPLETED",
        bytesUploaded: file.size,
        totalBytes: file.size,
        percentage: 100,
        currentPart: session.totalParts,
        totalParts: session.totalParts,
        file,
        fileMetadata: completeResponse.data,
        error: null,
      });

      return completeResponse.data;
    },
    [persistSession, scope, setUploadState, uploadPart, user?.token]
  );

  const ensureCompletedUpload = useCallback(
    async (file: File | null, context: MultipartUploadContext) => {
      if (!file || !multipartEnabled) {
        return null;
      }

      if (state.fileMetadata && state.status === "COMPLETED") {
        return state.fileMetadata;
      }

      if (uploadPromiseRef.current) {
        return uploadPromiseRef.current;
      }

      uploadPromiseRef.current = startUpload(file, context);

      try {
        return await uploadPromiseRef.current;
      } finally {
        uploadPromiseRef.current = null;
      }
    },
    [startUpload, state.fileMetadata, state.status]
  );

  return useMemo(
    () => ({
      state,
      multipartEnabled,
      ensureCompletedUpload,
      abortUpload: abort,
      resetUpload: reset,
    }),
    [abort, ensureCompletedUpload, reset, state]
  );
};
